import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTenants } from 'app/entities/tenant/tenant.reducer';
import { LabServiceType } from 'app/shared/model/enumerations/lab-service-type.model';
import { EntityType } from 'app/shared/model/enumerations/entity-type.model';
import { createEntity, getEntity, reset, updateEntity } from './tenant-association.reducer';

export const TenantAssociationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tenants = useAppSelector(state => state.tenant.entities);
  const tenantAssociationEntity = useAppSelector(state => state.tenantAssociation.entity);
  const loading = useAppSelector(state => state.tenantAssociation.loading);
  const updating = useAppSelector(state => state.tenantAssociation.updating);
  const updateSuccess = useAppSelector(state => state.tenantAssociation.updateSuccess);
  const labServiceTypeValues = Object.keys(LabServiceType);
  const entityTypeValues = Object.keys(EntityType);

  const handleClose = () => {
    navigate(`/tenant-association${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTenants({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.associatedDate !== undefined && typeof values.associatedDate !== 'number') {
      values.associatedDate = Number(values.associatedDate);
    }
    if (values.leavingDate !== undefined && typeof values.leavingDate !== 'number') {
      values.leavingDate = Number(values.leavingDate);
    }
    if (values.createdDtm !== undefined && typeof values.createdDtm !== 'number') {
      values.createdDtm = Number(values.createdDtm);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...tenantAssociationEntity,
      ...values,
      tenant: tenants.find(it => it.id.toString() === values.tenant?.toString()),
      associatedTenant: tenants.find(it => it.id.toString() === values.associatedTenant?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          labAssociationType: 'RADIOLOGY',
          tenantType: 'LAB',
          otherTenantType: 'LAB',
          ...tenantAssociationEntity,
          tenant: tenantAssociationEntity?.tenant?.id,
          associatedTenant: tenantAssociationEntity?.associatedTenant?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.tenantAssociation.home.createOrEditLabel" data-cy="TenantAssociationCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.tenantAssociation.home.createOrEditLabel">Create or edit a TenantAssociation</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="tenant-association-id"
                  label={translate('tinyMedicRestApp.tenantAssociation.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.associatedDate')}
                id="tenant-association-associatedDate"
                name="associatedDate"
                data-cy="associatedDate"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.internal')}
                id="tenant-association-internal"
                name="internal"
                data-cy="internal"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.leavingDate')}
                id="tenant-association-leavingDate"
                name="leavingDate"
                data-cy="leavingDate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.openingTime')}
                id="tenant-association-openingTime"
                name="openingTime"
                data-cy="openingTime"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.closeTime')}
                id="tenant-association-closeTime"
                name="closeTime"
                data-cy="closeTime"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.createdDtm')}
                id="tenant-association-createdDtm"
                name="createdDtm"
                data-cy="createdDtm"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.createdBy')}
                id="tenant-association-createdBy"
                name="createdBy"
                data-cy="createdBy"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.modifiedBy')}
                id="tenant-association-modifiedBy"
                name="modifiedBy"
                data-cy="modifiedBy"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.leftAssociation')}
                id="tenant-association-leftAssociation"
                name="leftAssociation"
                data-cy="leftAssociation"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.active')}
                id="tenant-association-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.cursor')}
                id="tenant-association-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.lastUpdated')}
                id="tenant-association-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.microSec')}
                id="tenant-association-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.timestampEntry')}
                id="tenant-association-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.labAssociationType')}
                id="tenant-association-labAssociationType"
                name="labAssociationType"
                data-cy="labAssociationType"
                type="select"
              >
                {labServiceTypeValues.map(labServiceType => (
                  <option value={labServiceType} key={labServiceType}>
                    {translate(`tinyMedicRestApp.LabServiceType.${labServiceType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.tenantType')}
                id="tenant-association-tenantType"
                name="tenantType"
                data-cy="tenantType"
                type="select"
              >
                {entityTypeValues.map(entityType => (
                  <option value={entityType} key={entityType}>
                    {translate(`tinyMedicRestApp.EntityType.${entityType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantAssociation.otherTenantType')}
                id="tenant-association-otherTenantType"
                name="otherTenantType"
                data-cy="otherTenantType"
                type="select"
              >
                {entityTypeValues.map(entityType => (
                  <option value={entityType} key={entityType}>
                    {translate(`tinyMedicRestApp.EntityType.${entityType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="tenant-association-tenant"
                name="tenant"
                data-cy="tenant"
                label={translate('tinyMedicRestApp.tenantAssociation.tenant')}
                type="select"
                required
              >
                <option value="" key="0" />
                {tenants
                  ? tenants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="tenant-association-associatedTenant"
                name="associatedTenant"
                data-cy="associatedTenant"
                label={translate('tinyMedicRestApp.tenantAssociation.associatedTenant')}
                type="select"
                required
              >
                <option value="" key="0" />
                {tenants
                  ? tenants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/tenant-association" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TenantAssociationUpdate;
